import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { UtilService } from '@service/util/util.service';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { UrlService } from '@service/url/url.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';

import {
	ANNUAL_VOLUME_DISPLAY,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	ProjectsTable,
	RestService,
	TruckSearchTable,
	Owner,
	PROJECT_CARRIER_MAP_STATUS,
	ProjectDTO,
	GET_LANE_PATH_STRING,
	SUBTRACT_FROM_TODAY,
	DATE_YMD,
} from 'cargo-chief-commons';
import { CarrierToProjectsMap } from '@domain/interface/carrier-to-projects-map';
import { PROJECT_TYPES } from '@domain/constants/project-types';
import { ProjectGroupService } from '@service/project-group/project-group.service';
import { ProjectUtilService } from '@service/project-util/project-util.service';
import { environment } from '@environment/environment';

@Component({
	selector: 'app-projects-tab-content',
	templateUrl: './projects-tab-content.component.html',
	styleUrls: ['./projects-tab-content.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsTabContentComponent implements OnInit, OnChanges {
	@Input() openedMatch: { carrierId: string } = null;
	@Input() searchTruckForm: any = null;
	@Input() truckLaneSearchId: string = null;
	@Input() truckSearchId: string = null;
	@Input() lanePricing: any = null;
	@Input() carrierProjectMap: any;
	@Input() isLoadingProjectsList = false;
	@Input() projectsNextPage = false;
	@Input() projectsViewList: Array<ProjectsTable> = null;
	@Input() capacitySearchResults: Array<TruckSearchTable> = [];
	@Input() projectsLimit = 10;
	@Input() allCarrierProjectMap: any = {};
	@Input() carriersProjectsList: any;
	@Input() truckSearchResult: boolean;
	@Input() truckSearchForm: any;
	@Input() usersList: Array<Owner> = [];
	@Input() addCarriersToProject!: ProjectDTO;
	@Input() showDragAndDropText: boolean = false;

	@Output() projectAdded: EventEmitter<ProjectDTO> = new EventEmitter();
	@Output() loadMoreProjects: EventEmitter<any> = new EventEmitter();
	@Output() carrierDropedToProject: EventEmitter<any> = new EventEmitter();

	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;
	IS_NULL_OR_UNDEFINED = IS_NULL_OR_UNDEFINED;
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	projectSearchText = '';

	selectedCarriersProjectMap: Array<CarrierToProjectsMap> = [];
	selectedCarriersProjectIds: Array<string> = [];
	showOnlySelectedCarriersProject = false;

	constructor(
		public utilService: UtilService,
		private segmentWrapper: SegmentWrapperService,
		private router: Router,
		private urlService: UrlService,
		private restService: RestService,
		private projectGroupService: ProjectGroupService,
		private projectUtilService: ProjectUtilService
	) {}

	ngOnInit(): void {}

	ngOnChanges() {
		if (this.searchTruckForm && this.projectsViewList) {
			const origin = this.searchTruckForm.origin.city + ', ' + this.searchTruckForm.origin.state;
			const destination =
				this.searchTruckForm.destination.city + ', ' + this.searchTruckForm.destination.state;

			this.projectsViewList.sort((projectA, projectB) => {
				return (
					ProjectsTabContentComponent.projectToNumber(origin, destination, projectB) -
					ProjectsTabContentComponent.projectToNumber(origin, destination, projectA)
				);
			});
		}
		this.initialiseSelectedMatch();
		if (IS_NOT_NULL_OR_UNDEFINED(this.openedMatch)) {
			this.buildSelectedCarriersProject();
		}
		if (IS_NULL_OR_UNDEFINED(this.openedMatch)) {
			this.showOnlySelectedCarriersProject = false;
		}
	}

	navigateToProjectDetails(projectId: String) {
		const project: ProjectsTable = this.projectsViewList.find((pro) => {
			return pro.id == projectId;
		});
		const payload = {
			user: this.utilService.getUserDataForSegmentEvent(),
			projectDTO: project,
		};
		this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.user_viewed_project, payload);
		this.router.navigate([`projects/project_details/${project.id}`]);
	}

	async drop(event: any, projectId: string) {
		event.preventDefault();
		const carrierIds: Array<string> = event.dataTransfer.getData('carrierId').split(',');
		const projectType: string = this.getProjectType(projectId);
		const projectTypesToCheck = [PROJECT_TYPES.WATERFALL.value, PROJECT_TYPES.DEDICATED_LANE.value];
		let selectedProjectGroupId = null;
		if (projectTypesToCheck.includes(projectType)) {
			let { projectGroupId, projectHasNoGroups } =
				await this.projectGroupService.selectProjectGroup(projectId);
			const projectHasGroups = !projectHasNoGroups;
			if (IS_NULL_OR_UNDEFINED(projectGroupId) && projectHasGroups) {
				this.utilService.showSnackbar(
					'Cannot add carrier to project without selecting a group',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				return;
			}
			selectedProjectGroupId = projectGroupId;
		}
		const carriersToAdd: Array<string> = [];
		carrierIds.map((carrierId) => {
			const carrierProjects: Array<any> = this.allCarrierProjectMap[carrierId];
			let projectIds: Array<string> = carrierProjects
				.filter((project) => project.isCarrierMappedToProject)
				.map((project) => project.projectId);
			projectIds = projectIds.concat(
				this.selectedCarriersProjectMap
					.filter((project) => project.isCarrierMappedToProject)
					.map((project) => project.projectId)
			);
			if (projectIds.indexOf(projectId) == -1) {
				carriersToAdd.push(carrierId);
			}
		});
		if (carriersToAdd.length == 0) {
			this.utilService.showSnackbar(
				'Carriers already procured for the project.',
				SNACKBAR.WARNING,
				SNACKBAR.ACTION
			);
			return;
		}
		this.carrierProjectToggled(projectId, carriersToAdd, selectedProjectGroupId);
	}

	carrierProjectToggled(
		projectId: string,
		carrierIds: Array<string>,
		projectGroupId: string = null
	) {
		const payload = {
			carrierIDs: carrierIds,
			status: PROJECT_CARRIER_MAP_STATUS.PROCURED,
			manageProjectGroup: IS_NULL_OR_UNDEFINED(projectGroupId),
			projectGroupId,
		};
		const url = this.urlService.addProjectCarrierMap(projectId);
		this.restService.POST(url, payload).subscribe(
			(result) => {
				let projectCarrierCount: number = result.length;
				result = `${result.length} carriers added to project`;
				this.utilService.showSnackbar(result, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
				this.carrierDropedToProject.emit({ projectId, carrierIds, projectCarrierCount });
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to update carrier to projects now.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			}
		);
	}

	fireSegmentEvent(projectId: string, carrierId: string, searchResult: TruckSearchTable) {
		const payload = {
			carrier: {
				businessName: searchResult.businessName,
				carrierId: carrierId,
			},
			projectId: projectId,
			page: 'Capacity Search',
		};
		this.segmentWrapper.fireSegmentEvent(
			SegmentEventsConstants.user_added_carrier_to_projects,
			payload
		);
	}

	viewMoreProjects() {
		if (this.projectsViewList.length <= this.projectsLimit) {
			this.isLoadingProjectsList = true;
			setTimeout(() => {
				this.loadMoreProjects.emit();
			}, 0);
		}
		this.projectsLimit += 10;
	}

	initialiseSelectedMatch() {
		this.selectedCarriersProjectIds = [];
		this.selectedCarriersProjectMap = [];
	}

	static projectToNumber(origin, destination, project): number {
		let output = 0;

		if (project?.name?.includes(origin) || project?.lane?.includes(origin)) {
			output += 0.5;
		}

		if (project?.name?.includes(destination) || project?.lane?.includes(destination)) {
			output += 0.5;
		}

		return output;
	}

	buildSelectedCarriersProject() {
		if (this.searchTruckForm && this.carrierProjectMap) {
			const origin = this.searchTruckForm.origin.city + ', ' + this.searchTruckForm.origin.state;
			const destination =
				this.searchTruckForm.destination.city + ', ' + this.searchTruckForm.destination.state;

			this.carrierProjectMap.sort((projectA, projectB) => {
				return (
					ProjectsTabContentComponent.projectToNumber(origin, destination, projectB) -
					ProjectsTabContentComponent.projectToNumber(origin, destination, projectA)
				);
			});
		}
		const carrierProject: any = this.carriersProjectsList?.find(
			(carrierProject) => carrierProject.carrierId === this.openedMatch.carrierId
		);
		carrierProject?.projectDTOList?.map((project: ProjectDTO) => {
			const projectMap = {} as CarrierToProjectsMap;
			projectMap.projectId = project.id;
			projectMap.carrierId = this.openedMatch.carrierId;
			projectMap.isCarrierMappedToProject = true;
			projectMap.projectName = project.name;
			projectMap.lane = GET_LANE_PATH_STRING(project);
			projectMap.customer = project?.customerDTO?.name;
			projectMap.isProjectNameDisplayed = projectMap.lane == project.name;
			projectMap.carrierCount = project.carrierCount;
			projectMap.type = project.type;
			if (
				IS_NOT_NULL_OR_UNDEFINED(project.loadVolume) &&
				IS_NOT_NULL_OR_UNDEFINED(project.loadVolumeDuration)
			) {
				const annualVolume = ANNUAL_VOLUME_DISPLAY(project);
				if (IS_NOT_NULL_OR_UNDEFINED(annualVolume)) {
					projectMap.monthlyVolumeDisplay = `${Math.round(annualVolume / 12)}/Month`;
				}
			}
			this.selectedCarriersProjectIds.push(projectMap.projectId);
			this.selectedCarriersProjectMap.push(projectMap);
		});
	}

	getProjectType(projectId: string): string {
		if (
			IS_NOT_NULL_OR_UNDEFINED(this.selectedCarriersProjectMap) &&
			this.selectedCarriersProjectMap.length > 0
		) {
			return this.selectedCarriersProjectMap.find((projectMap) => projectMap.projectId == projectId)
				.type;
		} else if (
			IS_NOT_NULL_OR_UNDEFINED(this.carrierProjectMap) &&
			this.carrierProjectMap.length > 0
		) {
			return this.carrierProjectMap.find((projectMap) => projectMap.projectId == projectId).type;
		} else if (
			IS_NOT_NULL_OR_UNDEFINED(this.projectsViewList) &&
			this.projectsViewList.length > 0
		) {
			return this.projectsViewList.find((projectView) => projectView.id == projectId).type;
		}
		return null;
	}

	async projectAddedOrUpdated(project: ProjectDTO) {
		await this.projectUtilService.showAddCarriersDialog({
			startDate: DATE_YMD(SUBTRACT_FROM_TODAY(environment.TRANSACTIONS_LOOKUP_WINDOW, 'months')),
			endDate: null,
			originCity: project.originCity,
			originState: project.originState,
			destinationCity: project.destCity,
			destinationState: project.destState,
			loadsCount: null,
			customerId: project.customerDTO?.id,
			projectIdList: [project.id],
		});
		this.projectAdded.emit(project);
	}
}
